import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, SmallTitleSpan, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useWMS1022_R01Store } from "./Store_WMS1022_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { WMS1022_R01DetailDto } from "./Dto_WMS1022_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { getItemPopupList } from "src/apis/itemApi";
import { ItemDto } from "src/dtos/item/itemDto";
import { showWarning } from "src/utils/toast/toastUtil";

// 범용 훅 임포트
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'WMS1022_R01-detail-grid-column-state';

interface Props {
	detailGridRef: React.RefObject<AgGridReact<WMS1022_R01DetailDto> | null>;
	defaultWMS1022_R01DetailRow: WMS1022_R01DetailDto;
	handleFetchLotList: (itemCd: string) => void;
}

export const DetailTable_WMS1022_R01 = (props: Props) => {

	const { detailGridRef, defaultWMS1022_R01DetailRow, handleFetchLotList } = props;

	const {
		WMS1022_R01DetailList, setWMS1022_R01DetailList,
		updateWMS1022_R01DetailList,
		selectedWMS1022_R01DetailList, setSelectedWMS1022_R01DetailList,
		initSelectedWMS1022_R01DetailList,
		deletedWMS1022_R01DetailList,
		originalWMS1022_R01DetailList,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
		setSelectedItemCdForLot,
		setLotScrollTop,
		transportComplete,
		editWMS1022_R01,
	} = useWMS1022_R01Store();
	const { unitList, itemStatusList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: WMS1022_R01DetailList,
		defaultGridData: defaultWMS1022_R01DetailRow,
		selectedRow: selectedWMS1022_R01DetailList,
		originalGridData: originalWMS1022_R01DetailList,
		updateRow: (index: number, data: any) => updateWMS1022_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		orderUnit: '단위',
		itemStatus: '상태',
		orderQtyView: '주문잔량',
		updateTransportQty: '입고합계',
		packQty: '포장량',
		remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [''],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: [''],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<WMS1022_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedWMS1022_R01DetailList,
            deletedList: deletedWMS1022_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		
        selectedWMS1022_R01DetailList, deletedWMS1022_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'orderUnit', 'orderQty', 'transportQty', 'updateTransportQty', 'remark'];

	const baseColumnDefs: ColDef<WMS1022_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 80,			
			editable: false,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,			
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,			
			editable: false,
		},
		{
			headerName: '단위', colId: 'orderUnit', field: 'orderUnit', width: 50,			
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '상태', colId: 'itemStatus', field: 'itemStatus', width: 60,			
			...CommonCodeColumn.options(itemStatusList, !transportComplete, undefined, undefined),
		},
		{
			headerName: '주문잔량', colId: 'orderQtyView', field: 'orderQtyView', width: 80,			
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// {
		// 	headerName: '주문량', colId: 'orderQty', field: 'orderQty', width: 70,
		// 	editable: false,
		// 	comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		// 	valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		// },
		// {
		// 	headerName: '기입고량', colId: 'transportQty', field: 'transportQty', width: 80,
		// 	editable: false,
		// 	comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		// 	valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		// },
		{
			headerName: '입고합계', colId: 'updateTransportQty', field: 'updateTransportQty', width: 80,			
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '포장량', colId: 'packQty', field: 'packQty', width: 80,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 150,			
			editable: true,
		},
		// ...IndexParameterCell(),
	], [
		deletedWMS1022_R01DetailList, WMS1022_R01DetailList, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

const columnDefs: ColDef<WMS1022_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<WMS1022_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: WMS1022_R01DetailList,
		originalDataList: originalWMS1022_R01DetailList,
		updateRow: updateWMS1022_R01DetailList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});

	//단일클릭으로 변경
	const handleRowClick = useCallback((e: RowClickedEvent<WMS1022_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedWMS1022_R01DetailList.includes(rowData['valueIndex'] as any);

		if (isSelected) {
			setSelectedWMS1022_R01DetailList([]);
			setSelectedItemCdForLot(null);
		} else {
			setSelectedWMS1022_R01DetailList([rowData['valueIndex']] as any);
			setSelectedItemCdForLot(rowData['itemCd'] as string);
			handleFetchLotList(rowData['itemCd'] as string);
		}
		setLotScrollTop(null);
	}, [selectedWMS1022_R01DetailList, setSelectedWMS1022_R01DetailList, setSelectedItemCdForLot, handleFetchLotList, setLotScrollTop]);

	
	const commonInputColumns = ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'orderUnit', 'orderQty', 'transportQty', 'updateTransportQty', 'remark'];
	const cellClickActions: CustomCellClickAction<WMS1022_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				// 입고량과 비고는 편집 가능한 상태일 때만 클릭으로 편집 시작
				if (field === 'remark') {
					return commonInputColumns.includes(field);
				}
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(WMS1022_R01DetailList, 'valueIndex', cellClickActions);

	// 모듈별 설정
	const WMS1022Config = createModuleConfig.WMS1022();

	// 품목 검색 함수 주입 제거 - 기본 설정만 사용
	const fieldConfigWithSearch = WMS1022Config.fieldConfig;

	// 품목 검색 함수
const { searchExactCode } = useCodeSearch({});

	// 공통 상세 테이블 핸들러
	const detailTableHandlers = useCommonDetailTableHandlers({
		detailList: WMS1022_R01DetailList,
		updateDetailList: updateWMS1022_R01DetailList,
		originalDetailList: originalWMS1022_R01DetailList,
		fieldConfig: fieldConfigWithSearch,
		codeSearchConfig: WMS1022Config.codeSearchConfig,
		calculateAmount: (data: any) => calculateDetailAmount(data, {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			dcField: 'dcAmt',
			amountField: 'orderAmt',
			preventNegative: false
		}),
		setCodeSearchModalProps: setCodeSearchModalProps,
		gridRef: detailGridRef,
	});

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 품목 코드 더블클릭으로 품목 검색 모달 열기
	 * - 키다운 네비게이션 (엔터 시 수량 필드로 이동, 방향키 네비게이션)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<WMS1022_R01DetailDto>({
		detailList: WMS1022_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					modelNo: 'modelNo',    // 품번
					itemUnit: 'orderUnit', // 단위
					itemCost: 'orderPrice' // 단가
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "", orderUnit: "",
					orderPrice: 0, dcRate: 0, dcAmt: 0, orderAmt: 0
				},
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess) // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		keyDownConfig: {
			qtyField: 'orderQty',        // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<WMS1022_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'itemCd' || field === 'itemNm' || field === 'itemSpec',
			action: (e, foundData) => {
				//이미 등록된 정보일경우 팝업 열리지 않음
				if(editWMS1022_R01?.wmsOrderNo) return;
				// 품목 검색 기능 제거됨
				// settingCodeSearchModalProps('품목', foundData.itemCd!, false, 'itemCd', foundData, foundData.valueIndex!);
			}
		},
	], [editWMS1022_R01?.wmsOrderNo]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(WMS1022_R01DetailList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<WMS1022_R01DetailDto>) => {
		postSortHandler(params, WMS1022_R01DetailList, originalWMS1022_R01DetailList, setWMS1022_R01DetailList);
	}, [WMS1022_R01DetailList, originalWMS1022_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = WMS1022_R01DetailList
			.reduce((sum, item) => sum + (deletedWMS1022_R01DetailList.includes(item.itemCd) ? 0 : Number(item.orderQty) || 0), 0);

		const totalUpdateTransportQty : number = WMS1022_R01DetailList
			.reduce((sum, item) => sum + (deletedWMS1022_R01DetailList.includes(item.itemCd) ? 0 : Number(item.updateTransportQty) || 0), 0);
		return [{
			itemCd: WMS1022_R01DetailList.length.toLocaleString() + "건",
			// orderQty: totalQty,
			orderQtyView: totalQty,
			updateTransportQty: totalUpdateTransportQty,
		}];	  
	}, [WMS1022_R01DetailList, deletedWMS1022_R01DetailList,]);

	return (
		<div className="w-full h-full flex flex-col gap-[4px]">
			<SmallTitleSpan> 입고 목록 </SmallTitleSpan>
			<GridContextWrapper<WMS1022_R01DetailDto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={detailGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setWMS1022_R01DetailList}
				initSelected={initSelectedWMS1022_R01DetailList}
				defaultRowData={defaultWMS1022_R01DetailRow}
				baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}	
				setColumnState={setDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<WMS1022_R01DetailDto>
					ref={detailGridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					
					defaultColDef={cellCssDefaultColDef}

					rowClassRules={createRowClassRules<WMS1022_R01DetailDto>({
						idKey: 'valueIndex',
						selectedList: selectedWMS1022_R01DetailList,
						deletedList: deletedWMS1022_R01DetailList
					})}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setDetailColumnState}		//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
				/>
			</GridContextWrapper>
		</div>
	)
}
